import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action, get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { reads } from 'macro-decorators';
import config from 'garaje/config/environment';

export default class EmployeesHrisSettingsController extends Controller {
  @service router;
  @service state;
  @service flashMessages;

  queryParams = ['syncToggleError'];

  /**
   * Set when the browser arrives back from app-store's `/integrations/sync-toggle/[id]`
   * after a failure. Surfaced as a flash by the route's `setupController`, then nulled.
   */
  @tracked syncToggleError = null;

  @reads('state.currentCompany') currentCompany;

  /**
   * Local UI state for the toggle — null when the user hasn't touched it
   * (so the rendered value falls back to the persisted config). Lets the
   * Save button distinguish "I want to enable" from "this is the current
   * state."
   */
  @tracked pendingDirectorySyncEnabled = null;

  /**
   * Any active OR pending HRIS plugin install. Pending is included here
   * (unlike the integrations-tile controller) because this page needs to
   * surface the "complete setup" state in Step 1.
   */
  get hrisPluginInstall() {
    return get(this.model, 'pluginInstalls')?.find((install) => {
      const status = get(install, 'status');
      const name = get(install, 'plugin.name') ?? '';
      return name.startsWith('HRIS') && (status === 'active' || status === 'pending');
    });
  }

  /**
   * Connection is "in place" when the plugin install has reached `active`
   * — i.e., the customer completed the install wizard (which includes
   * the HRIS/Merge connection step). Pending = wizard not finished.
   */
  get connectionVerified() {
    return get(this.hrisPluginInstall, 'status') === 'active';
  }

  get hasPendingInstall() {
    return get(this.hrisPluginInstall, 'status') === 'pending';
  }

  /**
   * Persisted "HRIS owns the directory-sync slot" state.
   *
   * The source of truth is `company.employees_sync_provider` — envoy-web
   * enforces the provider mutex + sync_mode/sync_state cascade on the
   * Company model, so this field is authoritative. Previously we mirrored
   * the same signal into `plugin_install.config.trackCompleteDirectorySync`,
   * but after EIP-3541 that flag is no longer written by the sync-toggle
   * flow (see envoy-app-store#1573), so reading it here returned stale
   * `false` and left the toggle looking dead after a save round-trip.
   */
  get persistedDirectorySyncEnabled() {
    return get(this.currentCompany, 'employeesSyncProvider') === 'hris';
  }

  /** What the toggle reflects — local pending value if set, else persisted. */
  get directorySyncEnabled() {
    return this.pendingDirectorySyncEnabled ?? this.persistedDirectorySyncEnabled;
  }

  /** Save button is enabled iff the customer has a pending change. */
  get hasUnsavedChange() {
    return (
      this.pendingDirectorySyncEnabled !== null &&
      this.pendingDirectorySyncEnabled !== this.persistedDirectorySyncEnabled
    );
  }

  /** True when a non-HRIS provider holds the slot. */
  get conflictingProvider() {
    const provider = get(this.currentCompany, 'employeesSyncProvider');
    return provider && provider !== 'hris' ? provider : null;
  }

  @action
  toggleSyncFlag(newValue) {
    this.pendingDirectorySyncEnabled = newValue;
  }

  /**
   * Save the toggle by hopping through app-store.
   *
   * envoy-web alone isn't enough: the Activepieces piece that actually polls Merge
   * reads its own copy of `trackCompleteDirectorySync` from the workflows project
   * store, and that copy is only ever written by app-store's saveInstall handler
   * (`WorkflowsManager#updateWorkflowsConfig`). app-store can't be fetched cross-
   * origin (different host, no shared session cookie), so we follow the existing
   * `/apps/dynamic` pattern: navigate the browser to app-store, do the work
   * same-origin where the NextAuth cookie is available, then redirect back here.
   *
   * The destination route lives at app-store: `/integrations/sync-toggle/[id]`.
   * It updates both stores and 302s back to `returnTo`.
   */
  goToSyncToggleTask = task(async () => {
    const install = this.hrisPluginInstall;
    if (!install || this.pendingDirectorySyncEnabled === null) {
      return;
    }
    const installId = get(install, 'id');
    const enabled = this.pendingDirectorySyncEnabled ? 'true' : 'false';
    // Preserve the full current URL — including `?revision=<sha>` used by
    // Garaje's feature-branch deploys. Dropping it lands the user on the
    // main-branch bundle where /employees/hris-settings doesn't yet exist,
    // and the router falls back to /employees.
    const returnTo = window.location.href;
    const url =
      `${config.appStoreHost}/integrations/sync-toggle/${installId}` +
      `?enabled=${enabled}&returnTo=${encodeURIComponent(returnTo)}`;
    window.location.href = url;
  });

  goToPluginSetupTask = task(async () => {
    const pluginKey = get(this.hrisPluginInstall, 'plugin.key');
    if (pluginKey) {
      await this.router.transitionTo('apps.dynamic', `by/${pluginKey}`);
      return;
    }
    await this.router.transitionTo('apps.dynamic', 'integrations');
  });

  @action
  goBack() {
    this.router.transitionTo('employees.integrations');
  }
}
